import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  computed,
  inject,
  signal,
} from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { Account, Application, ApplicationApi, License, LicenseApi, LicenseRequest } from '@venecia/apis/identity';
import { DateBlock, InputBlock } from '@venecia/molecules';
import { ConfirmDialogService, ErrorMessageService, MessageService, TraceService } from '@venecia/services';
import { DateUtil } from 'app/shared/utils/date.util';

@Component({
  selector: 'app-license-modal',
  imports: [CommonModule, DateBlock, InputBlock, ReactiveFormsModule, TranslateModule],
  templateUrl: './license-modal.html',
  styleUrl: './license-modal.scss',
})
export class LicenseModal implements OnChanges {
  private readonly applicationApi = inject(ApplicationApi);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly errorMessageService = inject(ErrorMessageService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly licenseApi = inject(LicenseApi);
  private readonly messageService = inject(MessageService);
  private readonly traceService = inject(TraceService);
  private readonly translateService = inject(TranslateService);

  @Input() license: License | null = null;
  @Input() open = false;
  @Input() preselectedAccountId: number | null = null;
  @Output() readonly closed = new EventEmitter<void>();
  @Output() readonly saved = new EventEmitter<void>();

  readonly availableAccounts = signal<readonly Account[]>([]);
  readonly availableApplications = signal<readonly Application[]>([]);
  readonly deleting = signal(false);
  readonly detailMode = signal<'create' | 'edit'>('create');
  readonly saving = signal(false);
  readonly selectedAccountId = signal<number | null>(null);
  readonly selectedApplicationId = signal<number | null>(null);
  readonly submitted = signal(false);

  readonly selectedCreatedAt = signal<string | null>(null);
  readonly selectedId = signal<number | null>(null);
  readonly selectedUpdatedAt = signal<string | null>(null);

  readonly isCreateMode = computed(() => this.detailMode() === 'create');
  readonly isEditMode = computed(() => this.detailMode() === 'edit');

  readonly form = this.formBuilder.nonNullable.group({
    activeFrom: ['', [Validators.required]],
    activeTo: ['', [Validators.required]],
    notes: ['', [Validators.required, Validators.maxLength(1024)]],
  }, { validators: this.activeToAtLeastOneDayAfterActiveFromValidator() });

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.open || (!changes['license'] && !changes['open'])) {
      return;
    }

    this.configureFromInputs();
  }

  closeDetailModal(force = false): void {
    if (!force && (this.saving() || this.deleting())) {
      return;
    }

    this.closed.emit();
  }

  async requestDelete(): Promise<void> {
    const id = this.selectedId();
    if (id === null) {
      return;
    }

    const keys = [
      'pages.licenses.confirm-delete.cancel',
      'pages.licenses.confirm-delete.confirm',
      'pages.licenses.confirm-delete.message',
      'pages.licenses.confirm-delete.title',
    ] as const;

    const i18n = await this.translateMap(keys);
    const confirmed = await this.confirmDialogService.open({
      cancelText: i18n['pages.licenses.confirm-delete.cancel'],
      confirmText: i18n['pages.licenses.confirm-delete.confirm'],
      message: i18n['pages.licenses.confirm-delete.message'],
      title: i18n['pages.licenses.confirm-delete.title'],
    });

    if (!confirmed) {
      return;
    }

    this.deleting.set(true);
    try {
      await firstValueFrom(this.licenseApi.delete(id));
      this.messageService.success(await this.translate('pages.licenses.messages.delete-success'));
      this.saved.emit();
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.licenses.delete-error'));
    } finally {
      this.deleting.set(false);
    }
  }

  async save(): Promise<void> {
    this.submitted.set(true);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const confirmed = await this.confirmSave();
    if (!confirmed) {
      return;
    }

    this.saving.set(true);
    try {
      const payload = this.toApiModel();

      if (this.isCreateMode()) {
        await firstValueFrom(this.licenseApi.create(payload));
        this.messageService.success(await this.translate('pages.licenses.messages.create-success'));
      } else {
        await firstValueFrom(this.licenseApi.update(this.selectedIdOrThrow(), payload));
        this.messageService.success(await this.translate('pages.licenses.messages.update-success'));
      }

      this.saved.emit();
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.licenses.save-error'));
    } finally {
      this.saving.set(false);
    }
  }

  hasError(controlName: keyof LicenseFormModel, errorName: string): boolean {
    return this.form.controls[controlName].hasError(errorName);
  }

  isInvalid(controlName: keyof LicenseFormModel): boolean {
    const control = this.form.controls[controlName];
    return (control.dirty || control.touched || this.submitted()) && control.invalid;
  }

  isDateRangeInvalid(): boolean {
    return (this.form.controls.activeTo.dirty || this.form.controls.activeTo.touched || this.submitted())
      && this.form.hasError('activeToMinOneDayAfterActiveFrom');
  }

  onAccountSelectionChange(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    const value = target?.value ?? '';
    this.selectedAccountId.set(value ? Number(value) : null);
  }

  onApplicationSelectionChange(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    const value = target?.value ?? '';
    this.selectedApplicationId.set(value ? Number(value) : null);
  }

  private configureFromInputs(): void {
    this.submitted.set(false);

    const license = this.license;
    if (!license) {
      this.availableAccounts.set([]);
      this.availableApplications.set([]);
      this.detailMode.set('create');
      this.selectedAccountId.set(this.preselectedAccountId);
      this.selectedApplicationId.set(null);
      this.selectedCreatedAt.set(null);
      this.selectedId.set(null);
      this.selectedUpdatedAt.set(null);

      this.form.reset({
        activeFrom: this.defaultDateTime(1, 0, 0),
        activeTo: this.defaultDateTime(2, 23, 9),
        notes: '',
      });
      void this.loadApplications();
      return;
    }

    this.availableAccounts.set([]);
    this.availableApplications.set([]);
    this.detailMode.set('edit');
    this.selectedAccountId.set(license.account?.id ?? null);
    this.selectedApplicationId.set(license.application?.id ?? null);
    this.selectedCreatedAt.set(license['created-at'] ?? null);
    this.selectedId.set(license.id);
    this.selectedUpdatedAt.set(license['updated-at'] ?? null);

    this.form.reset({
      activeFrom: license['active-from'] || this.defaultDateTime(1, 0, 0),
      activeTo: license['active-to'] || this.defaultDateTime(2, 23, 9),
      notes: license.notes,
    });

    void this.loadApplications();
  }

  private selectedIdOrThrow(): number {
    const id = this.selectedId();
    if (id === null) {
      throw new Error('License id is required for update');
    }
    return id;
  }

  private toApiModel(): LicenseRequest {
    const formValue = this.form.getRawValue();
    const accountId = this.selectedAccountId();
    const applicationId = this.selectedApplicationId();

    if (accountId === null) {
      throw new Error('Account id is required for license create/update');
    }

    if (applicationId === null) {
      throw new Error('Application id is required for license create/update');
    }

    return {
      account: { id: accountId },
      application: { id: applicationId },
      'active-from': DateUtil.toOffsetDateTime(formValue.activeFrom),
      'active-to': DateUtil.toOffsetDateTime(formValue.activeTo),
      notes: formValue.notes.trim(),
    };
  }

  private async loadApplications(): Promise<void> {
    try {
      const response = await firstValueFrom(this.applicationApi.search());
      this.availableApplications.set(response.body.data ?? []);
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.licenses.applications-load-error'));
    }
  }

  private async translate(key: string): Promise<string> {
    return firstValueFrom(this.translateService.get(key));
  }

  private async translateMap(keys: readonly string[]): Promise<Record<string, string>> {
    return firstValueFrom(this.translateService.get([...keys]));
  }

  private async confirmSave(): Promise<boolean> {
    const keys = [
      'common.confirm-save.cancel',
      'common.confirm-save.confirm',
      'common.confirm-save.message',
      'common.confirm-save.title',
    ] as const;

    const i18n = await this.translateMap(keys);
    return this.confirmDialogService.open({
      cancelText: i18n['common.confirm-save.cancel'],
      confirmText: i18n['common.confirm-save.confirm'],
      message: i18n['common.confirm-save.message'],
      title: i18n['common.confirm-save.title'],
    });
  }

  private defaultDateTime(daysFromToday: number, hour: number, minute: number): string {
    const date = new Date();
    date.setDate(date.getDate() + daysFromToday);
    date.setHours(hour, minute, 0, 0);
    return this.formatDateTime(date);
  }

  private formatDateTime(date: Date): string {
    const year = date.getFullYear();
    const month = this.padDatePart(date.getMonth() + 1);
    const day = this.padDatePart(date.getDate());
    const hours = this.padDatePart(date.getHours());
    const minutes = this.padDatePart(date.getMinutes());

    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }

  private padDatePart(value: number): string {
    return value.toString().padStart(2, '0');
  }

  private activeToAtLeastOneDayAfterActiveFromValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const activeFrom = this.parseDateTime(control.get('activeFrom')?.value);
      const activeTo = this.parseDateTime(control.get('activeTo')?.value);

      if (!activeFrom || !activeTo) {
        return null;
      }

      const minimumActiveToTime = activeFrom.getTime() + LicenseModal.ONE_DAY_IN_MILLISECONDS;
      return activeTo.getTime() >= minimumActiveToTime ? null : { activeToMinOneDayAfterActiveFrom: true };
    };
  }

  private parseDateTime(value: unknown): Date | null {
    if (typeof value !== 'string' || !value.trim()) {
      return null;
    }

    const normalized = value.trim().replace(' ', 'T');
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private static readonly ONE_DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
}

interface LicenseFormModel {
  activeFrom: string;
  activeTo: string;
  notes: string;
}
